import { FileCode2, ShieldOff } from '@orgus/ui/icons';
import { useMemo, useState } from 'react';
import { Badge, Button, ConfirmDialog, DataTable, EmptyState, Panel, RelativeTime, RowStatus, Segmented, Tooltip, formatInt, plural, toast } from '@orgus/ui';
import type { Column, RowStatusProps } from '@orgus/ui';
import type { DisciplineWaiver, Project } from '@orgus/protocol';
import { useDisciplineActions } from './actions.ts';
import { LAYOUT_WAIVERS_FILE, REPO_WAIVERS_FILE, repoFileWaivers, waiverDaysLeft, waiverStatus } from './feed.ts';
import type { WaiverStatus } from './feed.ts';
import type { RepoRow } from './model.ts';

interface Row {
  w: DisciplineWaiver;
  status: WaiverStatus;
  left: number;
  projectName: string;
}

/** A waiver's one status: only one about to run out asks for a person. */
export function waiverRowStatus(status: WaiverStatus, left: number): Pick<RowStatusProps, 'tone' | 'icon' | 'label'> {
  switch (status) {
    case 'expiring':
      return { tone: 'attention', icon: 'warning', label: left === 0 ? 'Last day' : `${formatInt(left)} d left` };
    case 'expired':
      return { tone: 'neutral', icon: 'ended', label: 'Expired' };
    case 'revoked':
      return { tone: 'neutral', icon: 'stopped', label: 'Revoked' };
    default:
      return { tone: 'neutral', icon: 'done', label: `${formatInt(left)} d left` };
  }
}

/**
 * Every waiver in one place: the ones kept in Orgus (audited, revocable here) and what each
 * repo's own .orgus/waivers.yaml waived in its last check. Both count in every score. The
 * folder layout gate keeps its exceptions apart, in .orgus/waivers.json, and says so.
 */
export function WaiversView({ waivers, projects, rows: repos, now }: { waivers: DisciplineWaiver[]; projects: Project[]; rows: readonly RepoRow[]; now: number }) {
  const actions = useDisciplineActions();
  const [show, setShow] = useState<'open' | 'all'>('open');
  const [revoking, setRevoking] = useState<Row | null>(null);
  const names = useMemo(() => new Map(projects.map((p) => [p.id, p.name])), [projects]);
  const all = useMemo<Row[]>(
    () => waivers.map((w) => ({ w, status: waiverStatus(w, now), left: waiverDaysLeft(w, now), projectName: names.get(w.projectId) ?? w.projectId })).sort((a, b) => a.left - b.left),
    [waivers, now, names],
  );
  const open = all.filter((r) => r.status === 'active' || r.status === 'expiring');
  const rows = show === 'open' ? open : all;
  const fromFiles = useMemo(() => repoFileWaivers(repos, waivers, now), [repos, waivers, now]);
  const fileWaived = fromFiles.reduce((n, r) => n + r.waived, 0);

  const columns: Column<Row>[] = [
    {
      key: 'status',
      header: 'Status',
      type: 'badge',
      sortValue: (r) => r.left,
      text: (r) => waiverRowStatus(r.status, r.left).label,
      cell: (r) => <RowStatus {...waiverRowStatus(r.status, r.left)} hideLabel="below-lg" />,
    },
    {
      key: 'what',
      header: 'Waiver',
      grow: true,
      text: (r) => `${r.w.rule} ${r.w.path} ${r.w.reason} ${r.projectName}`,
      sortValue: (r) => r.w.rule,
      cell: (r) => (
        <Tooltip content={r.w.reason} delay={500}>
          <span tabIndex={-1} className="flex min-w-0 items-baseline gap-3">
            <span className="shrink-0 font-mono text-ui text-fg">{r.w.rule}</span>
            <span data-meta="" className="min-w-0 truncate font-mono text-caption text-fg-muted">
              {r.w.path}
            </span>
            <span data-meta="" className="hidden min-w-0 truncate text-caption text-fg-subtle xl:inline">
              {r.w.reason}
            </span>
          </span>
        </Tooltip>
      ),
    },
    { key: 'repo', header: 'Repo', hideBelow: 'md', text: (r) => r.projectName, sortValue: (r) => r.projectName, cell: (r) => <span className="text-fg-muted">{r.projectName}</span> },
    { key: 'owner', header: 'Owner', hideBelow: 'lg', text: (r) => r.w.owner, sortValue: (r) => r.w.owner, cell: (r) => <span className="text-fg-muted">{r.w.owner}</span> },
    {
      key: 'expires',
      header: 'Expires',
      hideBelow: 'sm',
      sortValue: (r) => r.w.expires,
      cell: (r) => (
        <Tooltip content={`Granted by ${r.w.createdBy}`}>
          <span tabIndex={-1} className="text-caption text-fg-subtle num">
            {r.w.expires}
          </span>
        </Tooltip>
      ),
    },
    {
      key: 'granted',
      header: 'Granted',
      hideBelow: 'lg',
      type: 'date',
      sortValue: (r) => r.w.createdAt,
      cell: (r) => <RelativeTime ts={r.w.createdAt} className="text-caption text-fg-subtle" />,
    },
  ];
  const revoke = async () => {
    if (!revoking) return;
    // A failure shows inside the dialog.
    await actions.revokeWaiver(revoking.w.id);
    toast.success('Waiver revoked', 'The next check counts those violations again.');
    setRevoking(null);
  };
  return (
    <div className="flex flex-col gap-4">
      <p className="text-caption text-fg-subtle">A waiver skips one rule on one path until it expires.</p>

      <Panel
        title="Kept in Orgus"
        sub={open.length ? `${formatInt(open.length)} open` : undefined}
        flush
        actions={
          <Segmented
            size="sm"
            label="Show"
            value={show}
            onChange={setShow}
            options={[
              { value: 'open', label: 'Open', count: open.length },
              { value: 'all', label: 'All', count: all.length },
            ]}
          />
        }
      >
        {rows.length ? (
          <DataTable
            label="Waivers kept in Orgus"
            frameless
            rows={rows}
            columns={columns}
            rowKey={(r) => r.w.id}
            pageSize={false}
            rowActions={(r) =>
              r.status === 'active' || r.status === 'expiring' ? (
                <Button size="sm" variant="ghost" onClick={() => setRevoking(r)}>
                  Revoke waiver
                </Button>
              ) : null
            }
          />
        ) : (
          <EmptyState
            icon={<ShieldOff />}
            title={show === 'open' && all.length ? 'No open waivers' : 'No waivers kept in Orgus'}
            description="Waive a violation from the Violations tab."
          />
        )}
      </Panel>

      <Panel title={`In ${REPO_WAIVERS_FILE}`} sub={fileWaived ? `${plural(fileWaived, 'violation')} waived in the last checks` : undefined} flush>
        {fromFiles.length ? (
          <ul aria-label="Waived by repo files" className="flex flex-col divide-y divide-border-subtle">
            {fromFiles.map((f) => (
              <li key={f.projectId} data-row="" className="flex h-row items-center gap-3 px-row-x">
                <FileCode2 className="size-4 shrink-0 text-fg-subtle" aria-hidden />
                <span className="min-w-0 flex-1 truncate text-ui text-fg">{f.projectName}</span>
                <span data-meta="" className="hidden min-w-0 truncate text-caption text-fg-subtle md:inline">
                  {f.rules[0]!.title}
                </span>
                {f.rules.length > 1 && (
                  <Badge data-chip="" size="sm" variant="outline" className="hidden md:inline-flex">
                    +{f.rules.length - 1}
                  </Badge>
                )}
                <span className="w-24 shrink-0 text-right text-caption text-fg-muted num">{plural(f.waived, 'violation')}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="px-row-x py-3 text-caption text-fg-subtle">No repo waived anything with its own file in the last check.</p>
        )}
      </Panel>

      <p className="text-caption text-fg-subtle">
        Not counted here: the folder layout gate (<code className="font-mono">orgus layout</code>) keeps its own exceptions in <code className="font-mono">{LAYOUT_WAIVERS_FILE}</code>. They decide where
        files may live and never change a discipline score.
      </p>

      {revoking && (
        <ConfirmDialog
          open
          onOpenChange={(o) => !o && setRevoking(null)}
          title="Revoke this waiver?"
          description={`${revoking.w.rule} on ${revoking.w.path} in ${revoking.projectName} counts again from the next check.`}
          confirmLabel="Revoke waiver"
          tone="danger"
          onConfirm={revoke}
        />
      )}
    </div>
  );
}
